import React, { useEffect, useState } from "react";
import { Banner, Button, Chip, Layout, Text } from "@kidslivesafe/sc-react";

import "./deactivate-banner.css";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectMemberData,
  selectMemberExpired,
} from "../../../features/authentication/authenticationSlice";

import { parseDate } from "../../../utils";
import {
  getSubscriptionDetailsAsync,
  selectPrimarySubscription,
  undoCancelSubscriptionAsync,
} from "../../../features/subscriptions/subscriptionSlice";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";

const DeactivateBanner: React.FC = () => {
  const [displayBanner, setDisplayBanner] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const memberExpired = useAppSelector(selectMemberExpired);
  const memberInfo = useAppSelector(selectMemberData);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);

  useEffect(() => {
    if (subscriptionDetails?.cancelDate && !memberExpired) {
      setDisplayBanner(true);
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Cancellation Banner",
          EventAction: "Banner Display",
        }),
      );
    } else {
      setDisplayBanner(false);
    }
  }, [subscriptionDetails?.cancelDate, memberExpired]);

  const calculateDays = () => {
    if (memberInfo.accessExpirationDate) {
      const today = new Date();
      const cancelDate = new Date(memberInfo.accessExpirationDate);
      const diffTime = cancelDate.getTime() - today.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  return (
    <Banner
      showBanner={displayBanner}
      setShowBanner={setDisplayBanner}
      position="bottom"
      displayX={true}
      closeMiddleware={() => {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Cancellation Banner",
            EventAction: "Button Click",
            MetaData: JSON.stringify({
              "Close Banner": "Yes",
            }),
          }),
        );
      }}
    >
      <Layout
        type="grid"
        gridProps={{
          templateColumns: "2fr-1fr",
          alignItems: "center",
          justifyItems: "flex-end",
          gap: "xxs",
        }}
        breakpointStyles={{
          tablet: {
            type: "grid",
            gridProps: {
              templateColumns: "2fr-1fr",
              alignItems: "center",
              gap: "xxs",
            },
          },
          mobile: {
            type: "flex",
            flexProps: { direction: "column", gap: "xs" },
          },
        }}
      >
        <Layout
          type="flex"
          flexProps={{
            direction: "column",
            gap: "xs",
            alignItems: "flex-start",
          }}
          passThroughClassess="deactivate-banner-content--left"
        >
          <Chip chipText={`${calculateDays()} Days Left`} color="error" />

          <Text size="sm" fontWeight="bold">
            Access Expires:{" "}
            {memberInfo &&
              memberInfo.accessExpirationDate &&
              parseDate(memberInfo.accessExpirationDate, "utc:month-day-year")}
          </Text>
          <Text size="sm" textAlign="left">
            Keep your data, searches, and monitoring active. Reactivate now for
            uninterrupted access.
          </Text>
        </Layout>
        <Button
          handleClick={() => {
            dispatch(undoCancelSubscriptionAsync()).then(action => {
              dispatch(
                sendMemberEventAsync({
                  EventCategory: "Cancellation Banner",
                  EventAction: "Button Click",
                  MetaData: JSON.stringify({
                    "Membership Change": "Reactivate Membership",
                  }),
                }),
              );
              if (undoCancelSubscriptionAsync.fulfilled.match(action)) {
                dispatch(getSubscriptionDetailsAsync());
              }
            });
          }}
          buttonText="Reactivate Membership"
        />
      </Layout>
    </Banner>
  );
};

export default DeactivateBanner;
